import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import MapIcon from "@mui/icons-material/Map";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PhoneMissedOutlinedIcon from "@mui/icons-material/PhoneMissedOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { getManagers } from "../Admin/Utilities/api";
import { sendUserNotification } from "../../utilities";
import { PageHeader, PageShell } from "../../components/UI/Page";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../components/UI/States";
import LeadAnalytics from "./LeadAnalytics";
import LeadCard from "./LeadCard";
import LocationOverview from "./LocationOverview";
import {
  BRANCHES,
  avatarColor,
  buildLeadsCsv,
  derivePermissions,
  downloadCsv,
  fetchLeads,
  fetchSalesDirectory,
  initials,
  isSameMonth,
  leadMatchesBranch,
  leadMatchesPerson,
  monthsAgo,
  needsSalesperson,
  postAssignManager,
  postAssignSales,
  postNote,
  postProgress,
  postRemoveManager,
  postRemoveSales,
  searchLead,
  titleCase,
  wasContacted,
} from "./utilities";

const entryDateDesc = (a, b) =>
  new Date(b.webleadEntry?.entryDate) - new Date(a.webleadEntry?.entryDate);

// Segmented control (matches the MUI ToggleButtonGroup look from the theme).
const TabButton = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex min-h-[36px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-colors ${
      active
        ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/[0.14] dark:shadow-none"
        : "app-muted hover:text-[color:var(--app-text)]"
    }`}
  >
    {label}
    {count !== null && (
      <span
        className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold leading-none tabular-nums ${
          active
            ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
            : "bg-[color:var(--app-hover)]"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const QuickFilterChip = ({ label, active, tone, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex min-h-[32px] items-center rounded-full border px-3 text-xs font-semibold transition-colors ${
      active
        ? tone
        : "app-surface app-muted hover:text-[color:var(--app-text)]"
    }`}
  >
    {label}
  </button>
);

const EMPTY_TITLES = {
  Unassigned: "Queue is clear",
  Active: "No active leads",
  Completed: "No completed leads",
  Deleted: "No deleted leads",
};

const EMPTY_MESSAGES = {
  Unassigned: "The unassigned queue is empty. Every lead has a manager. 🎉",
  Active: "No active leads match the current filters.",
  Completed: "No completed leads match the current filters.",
  Deleted: "No deleted leads match the current filters.",
};

// Completed/Deleted grow forever; only pull this window up front and let
// the user opt into the full archive.
const HISTORY_MONTHS = 12;

const WebLeadsPage = () => {
  const user = useSelector(selectUser);
  const rolesState = useSelector(selectRoles);

  const roleList = useMemo(
    () => rolesState?.roles || user?.roles || [],
    [rolesState, user]
  );
  const permissions = useMemo(
    () => derivePermissions(roleList, user || {}),
    [roleList, user]
  );

  const [leads, setLeads] = useState({
    Active: null,
    Completed: null,
    Deleted: null,
    Unassigned: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [historyWindow, setHistoryWindow] = useState("recent"); // "recent" | "all"
  const [historyLoading, setHistoryLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [salesDirectory, setSalesDirectory] = useState([]);

  const [scopeBranch, setScopeBranch] = useState(null);
  const [activeTab, setActiveTab] = useState("Active");
  const [personFilter, setPersonFilter] = useState("ALL");
  const [quickFilter, setQuickFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(window.location.search).get("sq") || ""
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [toast, setToast] = useState(null);

  const initialized = useRef(false);
  const toastTimer = useRef(null);

  const notify = (message, tone = "success") => {
    clearTimeout(toastTimer.current);
    setToast({ message, tone });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  // ----------------------------------------------------------- initial scope

  useEffect(() => {
    if (!user || initialized.current) return;
    initialized.current = true;

    setScopeBranch(
      permissions.seesAllLocations
        ? "All Locations"
        : permissions.viewableBranches.includes(user.branch)
        ? user.branch
        : permissions.viewableBranches[0] || user.branch
    );
    if (permissions.worksUnassignedQueue) setActiveTab("Unassigned");
    // Plain users land on their own leads; anyone manager-like starts broad.
    if (!permissions.isManagerish && user.userID) setPersonFilter(user.userID);
  }, [user, permissions]);

  // ------------------------------------------------------------ data loading

  const loadLeads = async (branchForFetch, history = historyWindow) => {
    setLoading(true);
    setLoadError(null);

    // Stage 1 — the working queues the user sees first.
    try {
      const [active, unassigned] = await Promise.all([
        fetchLeads("Active", branchForFetch),
        permissions.seesUnassignedQueue
          ? fetchLeads("Unassigned", null)
          : Promise.resolve(null),
      ]);
      setLeads({
        Active: active,
        Completed: null,
        Deleted: null,
        Unassigned: unassigned,
      });
    } catch (err) {
      console.error("WebLeadsPage load:", err);
      setLoadError("Could not load web leads. Check your connection and retry.");
      setLoading(false);
      return;
    }
    setLoading(false);

    // Stage 2 — Completed/Deleted history streams in behind the render.
    setHistoryLoading(true);
    try {
      const since = history === "all" ? undefined : monthsAgo(HISTORY_MONTHS);
      const [completed, deleted] = await Promise.all([
        fetchLeads("Completed", branchForFetch, { since }),
        fetchLeads("Deleted", branchForFetch, { since }),
      ]);
      setLeads((prev) => ({ ...prev, Completed: completed, Deleted: deleted }));
    } catch (err) {
      console.error("WebLeadsPage history load:", err);
      notify("Couldn't load completed/deleted history.", "error");
      setLeads((prev) => ({
        ...prev,
        Completed: prev.Completed || [],
        Deleted: prev.Deleted || [],
      }));
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadFullHistory = async () => {
    setHistoryWindow("all");
    setHistoryLoading(true);
    try {
      const [completed, deleted] = await Promise.all([
        fetchLeads("Completed", fetchBranch),
        fetchLeads("Deleted", fetchBranch),
      ]);
      setLeads((prev) => ({ ...prev, Completed: completed, Deleted: deleted }));
    } catch (err) {
      console.error("WebLeadsPage full history:", err);
      notify("Could not load the full history.", "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Privileged roles pull everything once and filter client-side (branch
  // clicks on the overview shouldn't refetch); everyone else is fetched
  // already scoped to a single branch and refetches when it changes.
  const fetchBranch = permissions.seesAllLocations ? null : scopeBranch;
  const fetchKey = scopeBranch
    ? permissions.seesAllLocations
      ? "__all__"
      : scopeBranch
    : null;

  useEffect(() => {
    if (!fetchKey) return;
    loadLeads(fetchBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  useEffect(() => {
    if (!user) return;
    fetchSalesDirectory()
      .then(setSalesDirectory)
      .catch(() => notify("Could not load the sales directory.", "error"));
    if (permissions.canAssignManagers) getManagers(setManagers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, permissions.canAssignManagers]);

  // ------------------------------------------------------- optimistic writes

  const patchLead = (id, updater) =>
    setLeads((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        next[key] = prev[key]
          ? prev[key].map((l) => (l._id === id ? updater(l) : l))
          : prev[key];
      });
      return next;
    });

  const relocateLead = (patched, destinations) =>
    setLeads((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        next[key] = prev[key]
          ? prev[key].filter((l) => l._id !== patched._id)
          : prev[key];
        if (next[key] && destinations.includes(key)) {
          next[key] = [patched, ...next[key]].sort(entryDateDesc);
        }
      });
      return next;
    });

  const withRecovery = async (request, onFail) => {
    try {
      await request();
    } catch (err) {
      console.error("WebLeadsPage action:", err);
      notify(onFail || "That change didn't save — refreshing.", "error");
      loadLeads(fetchBranch);
    }
  };

  const actions = {
    setContact: (lead, contactType, status) => {
      patchLead(lead._id, (l) => ({
        ...l,
        leadProgress: {
          ...l.leadProgress,
          contactStatus: {
            ...l.leadProgress?.contactStatus,
            [contactType]: {
              ...l.leadProgress?.contactStatus?.[contactType],
              status,
            },
          },
        },
      }));
      withRecovery(() =>
        postProgress({
          user: user.name,
          id: lead._id,
          type: contactType === "phoneContact" ? "phone" : "email",
          status,
          reason: null,
        })
      );
    },

    setSaleNumber: (lead, type, value) => {
      patchLead(lead._id, (l) => ({
        ...l,
        saleData: {
          ...l.saleData,
          [type === "quote" ? "quoteNumber" : "orderNumber"]: value,
        },
      }));
      withRecovery(() =>
        postProgress({
          user: user.name,
          id: lead._id,
          type,
          status: value,
          reason: null,
        })
      );
    },

    complete: (lead) => {
      const patched = {
        ...lead,
        leadProgress: {
          ...lead.leadProgress,
          leadStatus: {
            ...lead.leadProgress?.leadStatus,
            completed: { status: 1, date: new Date().toISOString(), by: user.name },
          },
        },
      };
      relocateLead(patched, ["Completed"]);
      notify(`Marked ${lead.webleadEntry?.customerName || "lead"} completed.`);
      withRecovery(() =>
        postProgress({
          user: user.name,
          id: lead._id,
          type: "completed",
          status: 1,
          reason: null,
        })
      );
    },

    remove: (lead) => {
      const patched = {
        ...lead,
        leadProgress: {
          ...lead.leadProgress,
          leadStatus: {
            ...lead.leadProgress?.leadStatus,
            deleted: {
              status: 1,
              date: new Date().toISOString(),
              by: user.name,
              reason: `System Delete From ${user.name}`,
            },
          },
        },
      };
      relocateLead(patched, ["Deleted"]);
      notify(`Deleted ${lead.webleadEntry?.customerName || "lead"}. It can be restored.`);
      withRecovery(() =>
        postProgress({
          user: user.name,
          id: lead._id,
          type: "deleted",
          status: 1,
          reason: `System Delete From ${user.name}`,
        })
      );
    },

    restore: (lead, fromTab) => {
      const clearedFlag = fromTab === "Deleted" ? "deleted" : "completed";
      const patched = {
        ...lead,
        leadProgress: {
          ...lead.leadProgress,
          leadStatus: {
            ...lead.leadProgress?.leadStatus,
            [clearedFlag]: {
              ...lead.leadProgress?.leadStatus?.[clearedFlag],
              status: 0,
            },
          },
        },
      };
      const status = patched.leadProgress.leadStatus;
      const destinations = status.deleted?.status
        ? ["Deleted"]
        : status.completed?.status
        ? ["Completed"]
        : patched.assignInfo?.managerAssigned
        ? ["Active"]
        : ["Active", "Unassigned"];
      relocateLead(patched, destinations);
      notify(`Restored ${lead.webleadEntry?.customerName || "lead"}.`);
      withRecovery(() =>
        postProgress({
          user: user.name,
          id: lead._id,
          type: clearedFlag,
          status: 0,
          reason: null,
        })
      );
    },

    assignManager: (lead, person) => {
      // Mirrors the server: assigning a manager resets the whole assignInfo.
      const patched = {
        ...lead,
        assignInfo: {
          managerAssigned: 1,
          managerAssignDate: new Date().toISOString(),
          assignedManager: {
            UserID: person.UserID,
            Branch: person.Branch,
            EmailAddress: person.EmailAddress,
            Name: person.Name,
          },
        },
      };
      relocateLead(patched, ["Active"]);
      notify(`${titleCase(person.Name)} assigned as manager.`);
      if (person.EmailAddress && person.EmailAddress !== user?.email) {
        sendUserNotification(
          person.EmailAddress,
          user?.name || "System",
          `You have a new web lead from ${
            lead.webleadEntry?.customerName || "a customer"
          } to hand off.`,
          "New Web Lead Assignment",
          "/webleads"
        );
      }
      withRecovery(() =>
        postAssignManager({
          id: lead._id,
          UserID: person.UserID,
          Branch: person.Branch,
          EmailAddress: person.EmailAddress,
          Name: person.Name,
          assignedBy: user,
        })
      );
    },

    assignSales: async (lead, person) => {
      try {
        const response = await postAssignSales({
          id: lead._id,
          UserID: person.UserID,
          Branch: person.Branch,
          EmailAddress: person.EmailAddress,
          Name: person.Name,
          assignedBy: user,
        });
        if (response.message === "User already assigned") {
          notify(`${titleCase(person.Name)} is already on this lead.`, "error");
          return;
        }
        patchLead(lead._id, (l) => ({
          ...l,
          assignInfo: {
            ...l.assignInfo,
            salesAssigned: 1,
            lastSalesAssignDate: new Date().toISOString(),
            assignedSales: [
              ...(l.assignInfo?.assignedSales || []),
              {
                UserID: person.UserID,
                Branch: person.Branch,
                EmailAddress: person.EmailAddress,
                Name: person.Name,
                assignDate: new Date().toISOString(),
              },
            ],
          },
        }));
        notify(`${titleCase(person.Name)} assigned to the lead.`);
        if (person.EmailAddress && person.EmailAddress !== user?.email) {
          sendUserNotification(
            person.EmailAddress,
            user?.name || "System",
            `You have been assigned a new web lead from ${
              lead.webleadEntry?.customerName || "a customer"
            }.`,
            "New Web Lead Assignment",
            "/webleads"
          );
        }
      } catch (err) {
        console.error("WebLeadsPage assignSales:", err);
        notify("Could not assign the salesperson — refreshing.", "error");
        loadLeads(fetchBranch);
      }
    },

    removeSales: (lead, person) => {
      patchLead(lead._id, (l) => ({
        ...l,
        assignInfo: {
          ...l.assignInfo,
          assignedSales: (l.assignInfo?.assignedSales || []).filter(
            (s) => s?.UserID !== person?.UserID
          ),
        },
      }));
      notify(`${titleCase(person?.Name || "")} removed from the lead.`);
      withRecovery(() =>
        postRemoveSales({
          id: lead._id,
          UserID: person.UserID,
          Name: person.Name,
          assignedBy: user,
        })
      );
    },

    removeManager: (lead, person) => {
      const patched = {
        ...lead,
        assignInfo: {
          ...lead.assignInfo,
          managerAssigned: 0,
          assignedManager: {},
          managerAssignDate: null,
        },
      };
      relocateLead(patched, ["Active", "Unassigned"]);
      notify(`${titleCase(person?.Name || "")} removed — lead is back in the queue.`);
      withRecovery(() =>
        postRemoveManager({
          id: lead._id,
          UserID: person.UserID,
          Name: person.Name,
          assignedBy: user,
        })
      );
    },

    addNote: (lead, message) => {
      patchLead(lead._id, (l) => ({
        ...l,
        notes: [
          ...(l.notes || []),
          { note: message, username: user.name, note_date: new Date().toISOString() },
        ],
      }));
      withRecovery(() =>
        postNote({
          id: lead._id,
          user: user.name,
          timestamp: new Date(),
          message,
        })
      );
    },
  };

  // ------------------------------------------------------------ derived data

  const scoped = useMemo(() => {
    const applyBranch = (list) =>
      list
        ? scopeBranch && scopeBranch !== "All Locations"
          ? list.filter((l) => leadMatchesBranch(l, scopeBranch))
          : list
        : null;
    return {
      Active: applyBranch(leads.Active),
      Completed: applyBranch(leads.Completed),
      Deleted: applyBranch(leads.Deleted),
      Unassigned: leads.Unassigned, // company-wide queue, never branch-scoped
    };
  }, [leads, scopeBranch]);

  const tabs = useMemo(() => {
    const list = [];
    if (permissions.seesUnassignedQueue) list.push("Unassigned");
    list.push("Active", "Completed");
    if (permissions.canSeeDeleted) list.push("Deleted");
    return list;
  }, [permissions]);

  const filteredByPerson = useMemo(() => {
    const applyPerson = (list) =>
      list && personFilter !== "ALL"
        ? list.filter((l) => leadMatchesPerson(l, personFilter))
        : list;
    return {
      Unassigned: scoped.Unassigned,
      Active: applyPerson(scoped.Active),
      Completed: applyPerson(scoped.Completed),
      Deleted: applyPerson(scoped.Deleted),
    };
  }, [scoped, personFilter]);

  const visibleLeads = useMemo(() => {
    let list = filteredByPerson[activeTab] || [];
    if (activeTab === "Active" && quickFilter === "noContact")
      list = list.filter((l) => !wasContacted(l));
    if (activeTab === "Active" && quickFilter === "needsSales")
      list = list.filter(needsSalesperson);
    if (searchQuery) list = list.filter((l) => searchLead(l, searchQuery));
    return list;
  }, [filteredByPerson, activeTab, quickFilter, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(visibleLeads.length / perPage));
  const currentPage = Math.min(page, pageCount);
  const pagedLeads = visibleLeads.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery, personFilter, quickFilter, scopeBranch, perPage]);

  const stats = useMemo(() => {
    const active = scoped.Active || [];
    const completed = scoped.Completed || [];
    return {
      active: scoped.Active ? active.length : null,
      awaitingContact: scoped.Active
        ? active.filter((l) => !wasContacted(l)).length
        : null,
      needsSales: scoped.Active ? active.filter(needsSalesperson).length : null,
      completedThisMonth: scoped.Completed
        ? completed.filter((l) =>
            isSameMonth(
              l.leadProgress?.leadStatus?.completed?.date ||
                l.webleadEntry?.entryDate
            )
          ).length
        : null,
      unassigned: leads.Unassigned ? leads.Unassigned.length : null,
    };
  }, [scoped, leads.Unassigned]);

  // Personal performance for salespeople — always their own leads, no matter
  // which team filter is active in the list below.
  const myStats = useMemo(() => {
    if (permissions.isManagerish || !user?.userID) return null;
    const mine = (list) =>
      (list || []).filter((l) => leadMatchesPerson(l, user.userID));
    const myActive = mine(scoped.Active);
    const myCompleted = mine(scoped.Completed);
    const won = myCompleted.filter((l) => l.saleData?.orderNumber);
    return {
      active: scoped.Active ? myActive.length : null,
      awaitingContact: scoped.Active
        ? myActive.filter((l) => !wasContacted(l)).length
        : null,
      quoted: scoped.Active
        ? myActive.filter((l) => l.saleData?.quoteNumber).length
        : null,
      winRate:
        scoped.Completed && myCompleted.length > 0
          ? `${Math.round((won.length / myCompleted.length) * 100)}%`
          : "—",
    };
  }, [permissions.isManagerish, user, scoped]);

  const branchStats = useMemo(() => {
    if (!permissions.seesAllLocations) return [];
    return BRANCHES.map((name) => {
      const active = (leads.Active || []).filter((l) =>
        leadMatchesBranch(l, name)
      );
      return {
        name,
        active: active.length,
        awaitingContact: active.filter((l) => !wasContacted(l)).length,
        needsSales: active.filter(needsSalesperson).length,
        completedThisMonth: (leads.Completed || []).filter(
          (l) =>
            leadMatchesBranch(l, name) &&
            isSameMonth(
              l.leadProgress?.leadStatus?.completed?.date ||
                l.webleadEntry?.entryDate
            )
        ).length,
      };
    });
  }, [permissions.seesAllLocations, leads]);

  const branchTeam = useMemo(() => {
    if (!scopeBranch || scopeBranch === "All Locations") return [];
    return salesDirectory
      .filter((p) => p.Branch === scopeBranch)
      .sort((a, b) => a.Name.localeCompare(b.Name));
  }, [salesDirectory, scopeBranch]);

  // Every tab the user can see, honouring the branch/person filters — the
  // search box and quick filters are deliberately ignored so the export is a
  // full book of leads rather than whatever is on screen.
  const exportCsv = () => {
    const groups = {};
    tabs.forEach((tab) => {
      groups[tab] = filteredByPerson[tab] || [];
    });
    const count = Object.values(groups).reduce((sum, l) => sum + l.length, 0);
    if (!count) {
      notify("Nothing to export for the current selection.", "error");
      return;
    }
    const slug = (scopeBranch || "all-locations")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`web-leads-${slug}-${stamp}.csv`, buildLeadsCsv(groups));
    notify(`Exported ${count} ${count === 1 ? "lead" : "leads"} to CSV.`);
  };

  if (!user) {
    return <LoadingState label="Loading web leads…" />;
  }

  const scopeLabel =
    scopeBranch === "All Locations"
      ? `All ${BRANCHES.length} locations`
      : scopeBranch;

  const ACTION_BTN = "app-btn app-btn-secondary";

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
    <PageShell size="2xl">
      {/* ---------------------------------------------------------- header */}
      <PageHeader
        icon={ContactMailIcon}
        tone="emerald"
        eyebrow="Sales & Leads"
        title="Web Leads"
        description={
          <>
            {scopeLabel}
            {personFilter !== "ALL" && personFilter === user.userID
              ? " · showing your leads"
              : ""}
            {!permissions.canEditLeads && (
              <span className="ml-2 inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/15 px-2.5 py-0.5 align-middle text-xs font-semibold text-violet-700 dark:text-violet-300">
                Monitoring · read-only
              </span>
            )}
          </>
        }
        actions={
          <>
            {/* Area managers pick a branch inside their region */}
            {!permissions.seesAllLocations &&
              permissions.viewableBranches.length > 1 && (
                <select
                  value={scopeBranch || ""}
                  onChange={(e) => {
                    setScopeBranch(e.target.value);
                    setPersonFilter("ALL");
                  }}
                  aria-label="Branch"
                  className="app-input h-10 rounded-full border px-4 text-sm font-medium"
                >
                  {permissions.viewableBranches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              )}
            {/* Same audience as the map route itself: Admin, Web Lead Manager,
                Executive Team. */}
            {permissions.seesAllLocations && (
              <Link
                to="/webleads/webleadmap"
                title="Plot these leads on the map"
                className={ACTION_BTN}
              >
                <MapIcon sx={{ fontSize: 18 }} />
                Map
              </Link>
            )}
            {permissions.isManagerish && (
              <button
                type="button"
                onClick={exportCsv}
                disabled={loading}
                title="Download every lead in view — status, assignment, contact and sale data"
                className={ACTION_BTN}
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
                Export CSV
              </button>
            )}
            <button
              type="button"
              onClick={() => loadLeads(fetchBranch)}
              disabled={loading}
              className={ACTION_BTN}
            >
              <RefreshIcon
                sx={{ fontSize: 18 }}
                className={loading ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </>
        }
      />

      <div className="space-y-6">
      {/* ------------------------------------------------------ stat tiles */}
      {myStats ? (
        /* Salesperson dashboard: their own numbers, whatever the filters say */
        <section>
          <p className="app-muted mb-3 text-sm">
            Hi {titleCase(user.name.split(/\s+/)[0])} — you have{" "}
            <span className="font-semibold text-[color:var(--app-text)] tabular-nums">
              {myStats.active ?? "…"}
            </span>{" "}
            active {myStats.active === 1 ? "lead" : "leads"}
            {myStats.awaitingContact > 0 && (
              <>
                ,{" "}
                <span className="font-semibold tabular-nums text-red-700 dark:text-red-300">
                  {myStats.awaitingContact}
                </span>{" "}
                still waiting on a first contact
              </>
            )}
            .
          </p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="My active leads"
              value={myStats.active ?? "—"}
              icon={ForumOutlinedIcon}
              tone="sky"
              active={
                activeTab === "Active" &&
                personFilter === user.userID &&
                !quickFilter
              }
              onClick={() => {
                setActiveTab("Active");
                setPersonFilter(user.userID);
                setQuickFilter(null);
              }}
            />
            <StatTile
              label="Awaiting my contact"
              value={myStats.awaitingContact ?? "—"}
              icon={PhoneMissedOutlinedIcon}
              tone={myStats.awaitingContact ? "rose" : "default"}
              active={
                activeTab === "Active" &&
                personFilter === user.userID &&
                quickFilter === "noContact"
              }
              onClick={() => {
                setActiveTab("Active");
                setPersonFilter(user.userID);
                setQuickFilter("noContact");
              }}
            />
            <StatTile
              label="Quoted"
              value={myStats.quoted ?? "—"}
              icon={RequestQuoteOutlinedIcon}
              tone="sky"
            />
            <StatTile
              label="Win rate"
              value={myStats.winRate ?? "—"}
              icon={EmojiEventsOutlinedIcon}
              tone="violet"
            />
          </div>
        </section>
      ) : (
        <div
          className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${
            permissions.seesUnassignedQueue ? "lg:grid-cols-5" : "lg:grid-cols-4"
          }`}
        >
          {permissions.seesUnassignedQueue && (
            <StatTile
              label="Unassigned queue"
              value={stats.unassigned ?? "—"}
              icon={InboxOutlinedIcon}
              tone={stats.unassigned ? "rose" : "default"}
              active={activeTab === "Unassigned"}
              onClick={() => setActiveTab("Unassigned")}
            />
          )}
          <StatTile
            label="Active leads"
            value={stats.active ?? "—"}
            icon={ForumOutlinedIcon}
            tone="sky"
          />
          <StatTile
            label="Awaiting contact"
            value={stats.awaitingContact ?? "—"}
            icon={PhoneMissedOutlinedIcon}
            tone={stats.awaitingContact ? "rose" : "default"}
            active={activeTab === "Active" && quickFilter === "noContact"}
            onClick={() => {
              setActiveTab("Active");
              setQuickFilter("noContact");
            }}
          />
          <StatTile
            label="Needs salesperson"
            value={stats.needsSales ?? "—"}
            icon={PersonAddAltOutlinedIcon}
            tone={stats.needsSales ? "amber" : "default"}
            active={activeTab === "Active" && quickFilter === "needsSales"}
            onClick={() => {
              setActiveTab("Active");
              setQuickFilter("needsSales");
            }}
          />
          <StatTile
            label="Completed this month"
            value={stats.completedThisMonth ?? "—"}
            icon={TaskAltIcon}
            tone="emerald"
          />
        </div>
      )}

      {/* ------------------------------------------- all-locations overview */}
      {permissions.seesAllLocations && !loading && !loadError && (
        <LocationOverview
          branchStats={branchStats}
          selectedBranch={scopeBranch}
          onSelectBranch={(branch) => {
            setScopeBranch(branch);
            setPersonFilter("ALL");
            if (activeTab === "Unassigned" && branch !== "All Locations")
              setActiveTab("Active");
          }}
        />
      )}

      {/* ------------------------------------------------------- analytics */}
      {permissions.seesAllLocations && !loading && !loadError && (
        <LeadAnalytics
          leads={scoped}
          historyLoading={historyLoading}
          scopeLabel={scopeLabel}
          showByLocation={scopeBranch === "All Locations"}
        />
      )}

      {/* -------------------------------------------------------- lead list */}
      <section>
        {/* Toolbar: list tabs, search, page size, then the filters. */}
        <div className="app-card mb-4 space-y-3 rounded-2xl border p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div
              role="group"
              aria-label="Lead list"
              className="flex max-w-full items-center gap-0.5 overflow-x-auto rounded-xl bg-[color:var(--app-hover)] p-[3px]"
            >
              {tabs.map((tab) => (
                <TabButton
                  key={tab}
                  label={tab}
                  count={filteredByPerson[tab] ? filteredByPerson[tab].length : null}
                  active={activeTab === tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setQuickFilter(null);
                  }}
                />
              ))}
            </div>

            <div className="relative min-w-[200px] flex-1">
              <SearchIcon
                fontSize="small"
                className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, email, phone, address, request…"
                aria-label="Search leads"
                className="app-input h-10 w-full rounded-xl border pl-9 pr-3 text-sm"
              />
            </div>

            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="app-input h-10 rounded-xl border px-3 text-sm"
              title="Leads per page"
              aria-label="Leads per page"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>

          {/* Quick filters on the active queue */}
          {activeTab === "Active" && (
            <div className="flex flex-wrap items-center gap-2">
              <QuickFilterChip
                label="All active"
                active={!quickFilter}
                tone="border-sky-500/30 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                onClick={() => setQuickFilter(null)}
              />
              <QuickFilterChip
                label="Awaiting contact"
                active={quickFilter === "noContact"}
                tone="border-red-500/30 bg-red-500/15 text-red-700 dark:text-red-300"
                onClick={() =>
                  setQuickFilter(quickFilter === "noContact" ? null : "noContact")
                }
              />
              <QuickFilterChip
                label="Needs salesperson"
                active={quickFilter === "needsSales"}
                tone="border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                onClick={() =>
                  setQuickFilter(quickFilter === "needsSales" ? null : "needsSales")
                }
              />
            </div>
          )}

          {/* Branch team filter */}
          {branchTeam.length > 0 && activeTab !== "Unassigned" && (
            <div className="app-divider flex items-center gap-2 overflow-x-auto border-t pb-0.5 pt-3">
              <span className="app-eyebrow shrink-0 pr-1">Team</span>
              <button
                type="button"
                onClick={() => setPersonFilter("ALL")}
                aria-pressed={personFilter === "ALL"}
                className={`inline-flex min-h-[32px] shrink-0 items-center rounded-full border px-3 text-xs font-semibold transition-colors ${
                  personFilter === "ALL"
                    ? "border-sky-500/30 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                    : "app-surface app-muted hover:text-[color:var(--app-text)]"
                }`}
              >
                Everyone
              </button>
              {branchTeam.map((person) => {
                const isMe = person.UserID === user.userID;
                const active = personFilter === person.UserID;
                return (
                  <button
                    type="button"
                    key={person.UserID}
                    onClick={() =>
                      setPersonFilter(active ? "ALL" : person.UserID)
                    }
                    aria-pressed={active}
                    title={titleCase(person.Name)}
                    className={`inline-flex min-h-[32px] shrink-0 items-center gap-1.5 rounded-full border py-0.5 pl-1 pr-3 text-xs font-semibold transition-colors ${
                      active
                        ? "border-sky-500/30 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                        : "app-surface hover:bg-[color:var(--app-hover)]"
                    }`}
                  >
                    <span
                      className={`${avatarColor(
                        person.Name
                      )} inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white`}
                    >
                      {initials(person.Name)}
                    </span>
                    {titleCase(person.Name.split(/\s+/)[0])}
                    {isMe ? " (me)" : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Archive window notice for the history tabs */}
        {(activeTab === "Completed" || activeTab === "Deleted") &&
          historyWindow === "recent" &&
          filteredByPerson[activeTab] && (
            <p className="app-muted mb-3 text-xs font-medium">
              Showing the last {HISTORY_MONTHS} months.{" "}
              <button
                type="button"
                onClick={loadFullHistory}
                disabled={historyLoading}
                className="app-link disabled:opacity-50"
              >
                {historyLoading ? "Loading…" : "Load full history"}
              </button>
            </p>
          )}

        {/* Cards */}
        {loading ? (
          <div className="app-card rounded-2xl border">
            <LoadingState label="Loading web leads…" />
          </div>
        ) : loadError ? (
          <div className="app-card rounded-2xl border">
            <EmptyState
              icon={ErrorOutlineIcon}
              title={loadError}
              action={
                <button
                  type="button"
                  onClick={() => loadLeads(fetchBranch)}
                  className="app-btn app-btn-primary"
                >
                  Retry
                </button>
              }
            />
          </div>
        ) : !filteredByPerson[activeTab] ? (
          /* Stage-2 history still streaming in */
          <div className="app-card rounded-2xl border">
            <LoadingState label={`Loading ${activeTab.toLowerCase()} leads…`} />
          </div>
        ) : pagedLeads.length === 0 ? (
          <div className="app-card rounded-2xl border">
            <EmptyState
              icon={searchQuery ? SearchOffIcon : InboxOutlinedIcon}
              title={EMPTY_TITLES[activeTab]}
              description={EMPTY_MESSAGES[activeTab]}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {pagedLeads.map((lead) => (
              <LeadCard
                key={lead._id}
                lead={lead}
                tab={activeTab}
                user={user}
                permissions={permissions}
                managers={managers}
                salesDirectory={salesDirectory}
                actions={actions}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && !loadError && visibleLeads.length > perPage && (
          <nav
            aria-label="Pagination"
            className="mt-5 flex flex-wrap items-center justify-between gap-3"
          >
            <p className="app-muted text-xs font-medium tabular-nums">
              Showing {(currentPage - 1) * perPage + 1}–
              {Math.min(currentPage * perPage, visibleLeads.length)} of{" "}
              {visibleLeads.length} leads
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
                title="Previous page"
                aria-label="Previous page"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1)
                .filter(
                  (n) =>
                    n === 1 ||
                    n === pageCount ||
                    Math.abs(n - currentPage) <= 2
                )
                .map((n, index, arr) => (
                  <React.Fragment key={n}>
                    {index > 0 && arr[index - 1] !== n - 1 && (
                      <span className="app-muted px-1 text-xs">…</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setPage(n)}
                      aria-current={n === currentPage ? "page" : undefined}
                      className={`h-9 min-w-[36px] rounded-full px-2 text-sm font-semibold tabular-nums transition-colors ${
                        n === currentPage
                          ? "bg-[color:var(--app-accent-solid)] text-white shadow-sm"
                          : "hover:bg-[color:var(--app-hover)]"
                      }`}
                    >
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === pageCount}
                className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
                title="Next page"
                aria-label="Next page"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </nav>
        )}
      </section>
      </div>

      {/* ------------------------------------------------------------ toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 z-50 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2"
        >
          <div
            className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-lg ${
              toast.tone === "error" ? "bg-red-600" : "bg-emerald-600"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </PageShell>
    </div>
  );
};

export default WebLeadsPage;
